export type Vector3 = { x: number; y: number; z: number };
export type Euler = { x: number; y: number; z: number };

export type GlbTransform = {
  position?: Vector3;
  rotation?: Euler;
  scale?: Vector3;
};

export type GlbModelEntry = {
  key: string;
  url: string;
  transform?: GlbTransform;
  playAnimations?: boolean;
};

export type GlbCameraConfig = {
  fov: number;
  near: number;
  far: number;
  initialPosition: Vector3;
  initialTarget: Vector3;
};

export type GlbControlsConfig = {
  target: Vector3;
  minDistance: number;
  maxDistance: number;
  minPolarAngle?: number;
  maxPolarAngle?: number;
  minAzimuthAngle?: number;
  maxAzimuthAngle?: number;
  enablePan?: boolean;
  enableZoom?: boolean;
  enableDamping?: boolean;
  dampingFactor?: number;
  autoRotate?: boolean;
  autoRotateSpeed?: number;
};

export type GlbDirectionalLight = {
  color: number;
  intensity: number;
  position: Vector3;
};

export type GlbEnvironmentConfig = {
  hemisphereLight: {
    skyColor: number;
    groundColor: number;
    intensity: number;
  };
  directionalLights: GlbDirectionalLight[];
  /** Background hex. Optional — overridden by top-level `bgTheme` when present. */
  backgroundColor?: number;
};

/** Background theme for artifact scenes — drives both renderer clearColor and scene background. */
export type GlbBgTheme = 'white' | 'black';

export const GLB_BG_THEME_HEX: Record<GlbBgTheme, number> = {
  white: 0xffffff,
  black: 0x000000,
};

export type GlbGroundPlaneConfig = {
  size: number;
  color: number;
  textureUrl?: string;
  textureRepeat?: number;
  opacity?: number;
  positionY?: number;
};

export type GlbPreviewConfig = {
  url: string;
  transform?: GlbTransform;
  style?: import('../sceneConfig').PreviewModelStyle;
  /** Duration in seconds for the preview fadeout after main models load (default: 2) */
  revealDuration?: number;
  /** Camera position during preview (overrides camera.initialPosition) */
  cameraPosition?: Vector3;
  /** Camera target during preview (overrides camera.initialTarget) */
  cameraTarget?: Vector3;
};

export type GlbRendererConfig = {
  /** Clear color hex. Optional — overridden by top-level `bgTheme` when present. */
  clearColor?: number;
  maxPixelRatio?: number;
  toneMapping?: number;
  toneMappingExposure?: number;
};

export type GlbCollisionConfig = {
  /** Camera collision sphere radius (default: 0.05) */
  cameraRadius?: number;
};

export type GlbSceneConfig = {
  id: string;
  /**
   * Single source of truth for scene background colour (white / black).
   * When set: drives both `renderer.clearColor` and `environment.backgroundColor`,
   * AND is used by `SceneBackgroundSwitcher` as the initial state.
   * When absent: falls back to `renderer.clearColor` / `environment.backgroundColor`
   * literal hex (used by top-level GLB scenes that aren't themed).
   */
  bgTheme?: GlbBgTheme;
  renderer: GlbRendererConfig;
  camera: GlbCameraConfig;
  controls: GlbControlsConfig;
  models: GlbModelEntry[];
  environment: GlbEnvironmentConfig & {
    envMapIntensity?: number;
  };
  groundPlane?: GlbGroundPlaneConfig;
  preview?: GlbPreviewConfig;
  /** Enable mesh-based BVH collision. Presence enables collision. */
  collision?: GlbCollisionConfig;
};
